import { action, observable } from 'mobx';
import { useEffect } from 'react';
import { fetchMatchConfig } from '@src/services/match-config';
import localMatchConfig from './config.json';
import { createMatchConfigViewModel, DEFAULT_RUNTIME } from './match-config';

class MatchBracketStore {
  @observable.ref viewModel = null;
  @observable activeStageId = '';
  @observable loading = false;
  @observable.ref loadError = null;
  @observable loadStatus = 'idle';

  runtime = { ...DEFAULT_RUNTIME };
  matchConfigUrl = '';
  refreshTimer = null;
  inFlightRequest = null;
  requestController = null;
  requestSequence = 0;
  disposed = false;
  hasSuccessfulData = false;
  hasShownErrorToast = false;

  constructor(options = {}) {
    this.fetchMatchConfigRequest = options.fetchMatchConfig || fetchMatchConfig;
  }

  get stages() {
    return this.viewModel ? this.viewModel.stages : [];
  }

  get activeStage() {
    return this.stages.find(stage => stage.id === this.activeStageId) || null;
  }

  get errorMessage() {
    return this.loadError ? this.loadError.message : '';
  }

  use() {
    useEffect(() => {
      this.disposed = false;
      return this.dispose;
    }, []);
  }

  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  createRequestController() {
    const AbortControllerClass = typeof window !== 'undefined' ? window.AbortController : undefined;
    return AbortControllerClass ? new AbortControllerClass() : null;
  }

  abortInFlightRequest() {
    if (this.requestController) {
      this.requestController.abort();
      this.requestController = null;
    }
  }
  scheduleNextRefresh() {
    this.clearRefreshTimer();
    if (!this.disposed && this.hasSuccessfulData && this.runtime.pollingEnabled) {
      this.refreshTimer = setTimeout(this.refreshMatchConfig, this.runtime.refreshIntervalMs);
    }
  }

  @action.bound
  setMatchConfigUrl(matchConfigUrl) {
    const nextMatchConfigUrl = typeof matchConfigUrl === 'string' ? matchConfigUrl.trim() : '';
    const shouldUseLocalFallback = !nextMatchConfigUrl;

    if (nextMatchConfigUrl === this.matchConfigUrl && (!shouldUseLocalFallback || this.viewModel)) {
      return false;
    }

    this.abortInFlightRequest();
    this.matchConfigUrl = nextMatchConfigUrl;

    if (shouldUseLocalFallback) {
      this.clearRefreshTimer();
      this.loading = false;
      this.commitViewModel(createMatchConfigViewModel(localMatchConfig));
      return true;
    }

    this.loadMatchConfig();
    return true;
  }

  @action.bound
  setActiveStage(stageId) {
    if (stageId === this.activeStageId || !this.stages.some(stage => stage.id === stageId)) {
      return false;
    }
    this.activeStageId = stageId;
    return true;
  }

  @action.bound
  startLoad() {
    this.loading = true;
    this.loadStatus = this.hasSuccessfulData ? 'refreshing' : 'loading';
  }

  @action.bound
  commitViewModel(nextViewModel) {
    this.runtime = nextViewModel.runtime;
    this.hasSuccessfulData = true;
    this.loadError = null;

    if (this.viewModel && this.viewModel.dataVersion === nextViewModel.dataVersion) {
      this.loadStatus = 'unchanged';
      return false;
    }

    const currentStageStillVisible = nextViewModel.stages.some(stage => stage.id === this.activeStageId);
    this.viewModel = nextViewModel;
    this.activeStageId = currentStageStillVisible ? this.activeStageId : nextViewModel.defaultStageId;
    this.loadStatus = 'success';
    return true;
  }

  @action.bound
  handleLoadError(error) {
    const fallbackMessage = this.viewModel ? this.viewModel.loadErrorText : '对阵信息加载失败，请稍后刷新';
    this.loadError = {
      code: error && error.code ? error.code : 'MATCH_CONFIG_LOAD_FAILED',
      message: error && error.message ? error.message : fallbackMessage
    };
    this.loadStatus = 'error';
    return this.loadError;
  }

  @action.bound
  handleRequestFailure(error) {
    const isInitialFallback = !this.viewModel;

    if (isInitialFallback) {
      this.commitViewModel(createMatchConfigViewModel(localMatchConfig));
    }

    const loadError = this.handleLoadError(error);

    if (!this.hasShownErrorToast) {
      this.hasShownErrorToast = true;
      // Toast.fail(this.errorMessage);
    }

    return loadError;
  }

  @action.bound
  finishLoad() {
    this.loading = false;
  }

  requestLatestConfig(requestId, signal) {
    return this.fetchMatchConfigRequest(this.matchConfigUrl, this.runtime.requestTimeoutMs, signal)
      .then(config => createMatchConfigViewModel(config))
      .then(nextViewModel => {
        if (this.disposed || requestId !== this.requestSequence) {
          return false;
        }
        return this.commitViewModel(nextViewModel);
      })
      .catch(error => {
        if (!this.disposed && requestId === this.requestSequence) {
          this.handleRequestFailure(error);
        }
        return false;
      });
  }

  loadMatchConfig() {
    if (this.disposed) {
      return Promise.resolve(false);
    }
    if (this.inFlightRequest) {
      return this.inFlightRequest;
    }

    this.clearRefreshTimer();
    const requestId = ++this.requestSequence;
    this.requestController = this.createRequestController();
    const signal = this.requestController ? this.requestController.signal : undefined;
    this.startLoad();
    const request = this.requestLatestConfig(requestId, signal).then(result => {
      if (!this.disposed && requestId === this.requestSequence) {
        this.inFlightRequest = null;
        this.requestController = null;
        this.finishLoad();
        this.scheduleNextRefresh();
      }
      return result;
    });
    this.inFlightRequest = request;
    return request;
  }

  @action.bound
  refreshMatchConfig() {
    return this.loadMatchConfig();
  }

  @action.bound
  dispose() {
    this.disposed = true;
    this.clearRefreshTimer();
    this.abortInFlightRequest();
    this.requestSequence++;
    this.inFlightRequest = null;
    this.loading = false;
  }
}

export default MatchBracketStore;
